import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  differenceInCalendarMonths,
  differenceInDays,
  addDays,
  addMonths,
  formatDuration
} from 'date-fns';
import { trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import {
  Modal,
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  HomeSummary,
  useMediatorModalHandle,
  Heading
} from '../../';

import { ListingCostsShape } from '../../constants/shapes';
import { isMobileScreen } from '../utils/breakpoints';
import {
  formatDisplayDate,
  getDateAsUTC,
  isDateValid,
  formatHomecardDateFullMonthYear
} from '../utils/dateUtils';

import PaymentDetails from '../PaymentDetails';

import styles from './PaymentInfoModal.module.scss';

const OPEN_PAYMENT_INFO_MODAL_CHANNEL = 'open_payment_info_modal';

const buildPaymentTimelineHeaderSubtitle = (checkIn, checkOut) => {
  if (
    !checkIn ||
    !checkOut ||
    !isDateValid(checkIn) ||
    !isDateValid(checkOut)
  ) {
    return undefined;
  }

  const startUTC = getDateAsUTC(checkIn);
  const endUTC = getDateAsUTC(checkOut);
  const endUTCInclusive = addDays(endUTC, 1);
  let months = differenceInCalendarMonths(endUTCInclusive, startUTC);
  let remainingDays = differenceInDays(
    endUTCInclusive,
    addMonths(startUTC, months)
  );

  if (remainingDays < 0) {
    months -= 1;
    remainingDays = differenceInDays(
      endUTCInclusive,
      addMonths(startUTC, months)
    );
  }

  const durationStr = formatDuration(
    { months, days: remainingDays },
    { delimiter: ' + ' }
  );

  return `${formatHomecardDateFullMonthYear(
    startUTC
  )} – ${formatHomecardDateFullMonthYear(endUTC)} · ${durationStr}`;
};

export const PaymentInfoModalButton = ({
  onOpen = () => {},
  dataTest = 'payment-info-open',
  linkText = '',
  linkSize = 'normal',
  linkColor = 'link',
  linkClassName = null,
  linkAlt = false,
  showSummaryInModal = false,
  linkFullWidth = false
}) => {
  const { handleModalOpen } = useMediatorModalHandle({
    channel: OPEN_PAYMENT_INFO_MODAL_CHANNEL
  });

  const handleOpen = () => {
    SoyuzAnalytics.sendGA4Event('summary_payment_open', {
      section: 'payment-sumary'
    });
    handleModalOpen(showSummaryInModal);
    onOpen();
  };

  return (
    <Button
      onClick={handleOpen}
      color={linkColor}
      size={linkSize}
      className={linkClassName}
      alt={linkAlt}
      dataTest={dataTest}
      fullWidth={linkFullWidth}
    >
      {linkText}
    </Button>
  );
};

PaymentInfoModalButton.propTypes = {
  onOpen: PropTypes.func,
  dataTest: PropTypes.string,
  linkText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  linkColor: PropTypes.oneOf([
    'primary',
    'secondary',
    'light',
    'danger',
    'help',
    'complementary',
    'filled',
    'select',
    'green-light',
    'white'
  ]),
  linkSize: PropTypes.oneOf(['normal', 'small', 'large', 'big']),
  linkClassName: PropTypes.string,
  linkAlt: PropTypes.bool,
  showSummaryInModal: PropTypes.bool,
  linkFullWidth: PropTypes.bool
};

export const PaymentInfoModal = ({
  onClose = () => {},
  downPaymentTimeInterval = undefined,
  hasNoDeposit = false,
  hasDepositProtection = false,
  rentableUnit = {},
  rating = '',
  showCheckOutDate = true,
  contractDurationType = 'temporal',
  hideDepositElanLaw = false,
  pricing,
  isInstantBooking = false,
  children
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const { isModalOpen, handleModalClose } = useMediatorModalHandle({
    channel: OPEN_PAYMENT_INFO_MODAL_CHANNEL,
    onOpen: showSummaryInModal => {
      setShowSummary(showSummaryInModal);
    }
  });

  const paymentTimelineHeaderSubtitle = buildPaymentTimelineHeaderSubtitle(
    pricing?.startContractDate,
    pricing?.endContractDate
  );

  const handleClose = () => {
    handleModalClose();
    onClose();
  };

  const currencyIsoCode =
    pricing?.currencyIsoCode || rentableUnit.currencyLabel;

  const paymentSection = (
    <PaymentDetails
      currencyIsoCode={currencyIsoCode}
      listingCosts={pricing?.listingCosts}
      pricing={pricing}
      downPaymentTimeInterval={downPaymentTimeInterval}
      isInstantBooking={isInstantBooking}
      hasNoDeposit={hasNoDeposit}
      hasDepositProtection={hasDepositProtection}
      contractDurationType={contractDurationType}
      hideDepositElanLaw={hideDepositElanLaw}
    />
  );

  return (
    <Modal
      ariaHideApp={false}
      contentLabel="modal"
      isOpen={isModalOpen}
      onRequestClose={handleClose}
      size={isMobileScreen() ? 'fullscreen' : 'medium'}
    >
      <ModalHeader
        dataTest="payment-info-close"
        label={
          showSummary
            ? trans('payment_details_modal.title.variant')
            : trans('payment_details.title')
        }
        subtitle={paymentTimelineHeaderSubtitle}
        onRequestClose={handleClose}
        className={styles['payment-info-modal__header']}
      />
      <ModalBody className={styles['payment-info-modal__body']}>
        {showSummary && (
          <HomeSummary
            title={rentableUnit.title}
            type={rentableUnit.type}
            photo={rentableUnit.photo || rentableUnit.mainPhotoUrl}
            rating={rating}
            photos={rentableUnit.photos}
          />
        )}
        {showSummary && (
          <div
            className={styles['payment-info-modal__dates']}
            data-test="spotahomeBookingPaymentInfo.dates"
          >
            <div className={styles['payment-info-modal__date-container']}>
              <span className={styles['payment-info-modal__date-title']}>
                {trans('payment_details_modal.move-in').toUpperCase()}
              </span>
              <span
                className={styles['payment-info-modal__date-value']}
                data-test="dates-move-in"
              >
                {formatDisplayDate(pricing.checkIn)}
              </span>
            </div>
            {pricing.checkOut && showCheckOutDate && (
              <div className={styles['payment-info-modal__date-container']}>
                <span className={styles['payment-info-modal__date-title']}>
                  {trans('payment_details_modal.move-out').toUpperCase()}
                </span>
                <span
                  className={styles['payment-info-modal__date-value']}
                  data-test="dates-move-out"
                >
                  {formatDisplayDate(pricing.checkOut)}
                </span>
              </div>
            )}
          </div>
        )}
        {showSummary ? (
          <Heading
            level={3}
            text={trans('payment_details_modal.payment_info')}
            type="capitalize"
            theme="dark"
            align="left"
          />
        ) : null}
        {paymentSection}
      </ModalBody>
      {children ? (
        <ModalFooter className="booking-payment-info-modal__footer">
          {children(handleClose)}
        </ModalFooter>
      ) : null}
    </Modal>
  );
};

PaymentInfoModal.propTypes = {
  onClose: PropTypes.func,
  pricing: PropTypes.shape({
    currencyIsoCode: PropTypes.string.isRequired,
    checkIn: PropTypes.string.isRequired,
    checkOut: PropTypes.string.isRequired,
    startContractDate: PropTypes.string.isRequired,
    endContractDate: PropTypes.string.isRequired,
    adminFee: PropTypes.number,
    downPayment: PropTypes.number.isRequired,
    reservationPrice: PropTypes.number.isRequired,
    tenantPayments: PropTypes.arrayOf(PropTypes.number || PropTypes.string)
      .isRequired,
    securityDeposit: PropTypes.number,
    securityDepositType: PropTypes.string,
    tenantFeeGross: PropTypes.number,
    offer: PropTypes.shape({
      discountType: PropTypes.string,
      discountValue: PropTypes.number
    }),
    listingCosts: ListingCostsShape
  }).isRequired,
  children: PropTypes.func.isRequired,
  downPaymentTimeInterval: PropTypes.shape({
    type: PropTypes.string,
    value: PropTypes.number
  }),
  rentableUnit: PropTypes.shape({
    title: PropTypes.string,
    photo: PropTypes.string,
    type: PropTypes.oneOf(['apartment', 'residence', 'room_shared', 'studio']),
    mainPhotoUrl: PropTypes.string,
    photos: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string }))
  }),
  rating: PropTypes.string,
  detailedAdminFee: PropTypes.shape({
    totalToPayOnMoveIn: PropTypes.number
  }),
  showCheckOutDate: PropTypes.bool,
  contractDurationType: PropTypes.string,
  hideDepositElanLaw: PropTypes.bool,
  hasNoDeposit: PropTypes.bool,
  hasDepositProtection: PropTypes.bool,
  isInstantBooking: PropTypes.bool
};

export default PaymentInfoModal;
